import React, { useEffect, useRef, useState } from 'react';
import mermaid, { MermaidConfig } from 'mermaid';

interface MermaidDiagramProps {
  content: string;
}

const config: MermaidConfig = {
  startOnLoad: false,
  theme: 'default',
  securityLevel: 'loose',
  fontFamily: 'inherit',
  secure: ['secure', 'securityLevel', 'startOnLoad', 'maxTextSize'] as string[],
};

export const MermaidDiagram: React.FC<MermaidDiagramProps> = ({ content }) => {
  const [svg, setSvg] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initializeMermaid = () => {
      try {
        mermaid.initialize(config);
        setIsInitialized(true);
      } catch (err) {
        console.error('Error initializing mermaid:', err);
        setError('Failed to initialize diagram renderer');
      }
    };

    initializeMermaid();
  }, []);

  useEffect(() => {
    if (!isInitialized) return;

    const renderDiagram = async () => {
      try {
        const id = `mermaid-${Math.random().toString(36).substr(2, 9)}`;
        const { svg } = await mermaid.render(id, content);
        setSvg(svg);
        setError(null);
      } catch (err) {
        console.error('Error rendering mermaid diagram:', err);
        setError('Failed to render diagram');
      }
    };

    void renderDiagram();
  }, [content, isInitialized]);

  if (error) {
    return (
      <div className="text-red-500 bg-red-50 p-4 rounded-md">
        {error}
        <pre className="mt-2 text-sm">{content}</pre>
      </div>
    );
  }

  return <div ref={containerRef} className="my-4 overflow-auto" dangerouslySetInnerHTML={{ __html: svg }} />;
};

export default MermaidDiagram;
